// İSG Portal — supplier (taşeron) auth akışları: tipler + iş servisleri.
//
// KAYNAK: api-contract.md §5.4 (supplier login), §5.5 (select-workspace), §5.6
// (switch-company), §5.10 (guest-activation). Taşeron portalı (web-supplier) tüm
// çok-firma + onboarding akışını bu modül üzerine kurar. `/auth/refresh` de
// principal-agnostiktir (§5.3) ve supplier terminal yanıtı döndürebilir — client'ın
// refresh dönüşünü tiplemesi için `SupplierAuthResponseDto` burada tanımlıdır.

import { api } from '../api/client';
import { useAuthStore } from './store';
import type { PrincipalType, TokenState } from './types';

/** Çok-firma seçim listesi öğesi — api-contract §5.4 (`SupplierCompanyItem`). */
export interface SupplierCompanyItem {
  id: number;
  uuid: string;
  name: string;
}

/**
 * Supplier auth ortak yanıtı — api-contract §5.4. `tokenState` iki durumu ayırır:
 * `terminal` (companyId + refreshToken dolu) veya `workspace_select` (transit;
 * refreshToken null, `companies` dolu).
 */
export interface SupplierAuthResponseDto {
  tokenState: TokenState;
  accessToken: string;
  refreshToken: string | null;
  tokenType: string;
  expiresIn: number;
  expiresAt: string;
  principalType: PrincipalType;
  companyId: number | null;
  accessibleCompanyIds: number[];
  companies?: SupplierCompanyItem[];
}

/** Supplier login isteği — api-contract §5.4 (`POST /auth/supplier/login`). */
export interface SupplierLoginRequest {
  /** E-posta **veya** telefon (normalize backend'de; tek input). */
  emailOrPhone: string;
  password: string;
}

/** Firma seçimi isteği — api-contract §5.5 (`POST /auth/select-workspace`). */
export interface SelectWorkspaceRequest {
  companyId: number;
}

/** Firma değiştirme isteği — api-contract §5.6 (`POST /auth/switch-company`). */
export interface SwitchCompanyRequest {
  companyId: number;
}

/** Guest-activation başlatma isteği — api-contract §5.10.2 (`POST /auth/guest/start`). */
export interface GuestStartRequest {
  token: string;
}

/** Guest-activation başlatma yanıtı — api-contract §5.10.2 (`GuestStartResult`). */
export interface GuestStartResult {
  /** Maskeli birincil hedef (örn. `y***@tasaron.example`) — geriye uyumlu tek değer. */
  maskedTarget: string;
  /** GL3 — kodun gönderildiği tüm iletişimler (ilk eleman = maskedTarget). */
  maskedTargets: string[];
  /** OTP son geçerlilik anı (UTC ISO 8601). */
  codeExpiresAt: string;
}

/** Guest-activation tamamlama isteği — api-contract §5.10.3 (`POST /auth/guest/activate`). */
export interface GuestActivateRequest {
  token: string;
  code: string;
  password: string;
  /** GL6 §4.7 — her bağlantı için zorunlu; OTP'den SONRA karşılaştırılır. */
  tcKimlikNo: string;
}

/**
 * Terminal supplier yanıtından erişilebilir firma listesini türetir. Terminal yanıt
 * firma **adı** taşımaz (`companies` yalnız `workspace_select`'te; §5.4) — bu yüzden
 * tek-firma girişte ad olmadan sentetik bir kayıt üretilir (switcher UI'ı `#id`'ye
 * düşer). Çok-firma yolunda adlar `select-workspace`'e `companies` ile taşınır.
 */
function terminalCompanies(dto: SupplierAuthResponseDto): SupplierCompanyItem[] {
  if (dto.companies && dto.companies.length > 0) {
    return dto.companies;
  }
  return dto.accessibleCompanyIds.map((id) => ({ id, uuid: '', name: '' }));
}

/**
 * Supplier login — api-contract §5.4. **Anonim** uç. Yanıt firma sayısına göre iki
 * durumdan biridir:
 *  - `terminal` (tek firma) → oturum burada kurulur (`setSupplierSession`).
 *  - `workspace_select` (çok firma) → oturum **kurulmaz**; çağıran transit token +
 *    `companies` ile firma seçim ekranına yönlendirir (`select-workspace` 2. adım).
 * Her iki durumda da ham DTO döner (çağıran `tokenState`'e göre dallanır).
 */
export async function supplierLogin(
  request: SupplierLoginRequest,
): Promise<SupplierAuthResponseDto> {
  const dto = await api.post<SupplierAuthResponseDto>('auth/supplier/login', {
    json: { emailOrPhone: request.emailOrPhone, password: request.password },
    auth: false,
  });

  if (dto.tokenState === 'terminal' && dto.refreshToken) {
    useAuthStore
      .getState()
      .setSupplierSession(
        { accessToken: dto.accessToken, refreshToken: dto.refreshToken },
        terminalCompanies(dto),
      );
  }
  return dto;
}

/**
 * Çok-firma girişinin 2. adımı — api-contract §5.5. **Transit token** header'da
 * taşınır (`bearer`; store'a yazılmaz, refresh üretmez). Seçilen firma erişilebilirse
 * terminal token döner ve oturum kurulur; firma adları switcher için saklanır.
 */
export async function selectWorkspace(
  companyId: number,
  transitToken: string,
  companies: SupplierCompanyItem[],
): Promise<SupplierAuthResponseDto> {
  const dto = await api.post<SupplierAuthResponseDto>('auth/select-workspace', {
    json: { companyId },
    auth: false,
    bearer: transitToken,
  });

  if (dto.refreshToken) {
    useAuthStore
      .getState()
      .setSupplierSession(
        { accessToken: dto.accessToken, refreshToken: dto.refreshToken },
        companies.length > 0 ? companies : terminalCompanies(dto),
      );
  }
  return dto;
}

/**
 * Firma değiştirme (switcher) — api-contract §5.6. **Terminal token** ile gelinir
 * (store access token). Hedef firma erişilebilirse yeni terminal token döner; erişim
 * yoksa `403 COMPANY_NOT_ACCESSIBLE`. Firma listesi (adlar) korunur.
 *
 * Çağıran (`CompanySwitcher`) başarıda TAM SAYFA RELOAD yapar (cache-juggling
 * yerine — bkz. plan: switch-company reload fix). Bu yüzden `updateTokens`'ın
 * persist yazımı burada `await` EDİLİR: bu fonksiyon dönmeden reload tetiklenirse
 * kullanıcı yeni token IndexedDB'ye yazılmadan önce yeniden başlatılmış olur ve
 * oturumdan düşer.
 */
export async function switchCompany(companyId: number): Promise<SupplierAuthResponseDto> {
  const dto = await api.post<SupplierAuthResponseDto>('auth/switch-company', {
    json: { companyId },
  });

  if (dto.refreshToken) {
    await useAuthStore
      .getState()
      .updateTokens({ accessToken: dto.accessToken, refreshToken: dto.refreshToken });
  }
  return dto;
}

/** Guest-activation başlat — api-contract §5.10.2 (`POST /auth/guest/start`). **Anonim.** */
export function guestStart(request: GuestStartRequest): Promise<GuestStartResult> {
  return api.post<GuestStartResult>('auth/guest/start', {
    json: { token: request.token },
    auth: false,
  });
}

/**
 * Guest-activation tamamla — api-contract §5.10.3 (`POST /auth/guest/activate`).
 * **Anonim.** Başarıda supplier terminal token döner (auto-login) ve oturum kurulur.
 */
export async function guestActivate(
  request: GuestActivateRequest,
): Promise<SupplierAuthResponseDto> {
  const dto = await api.post<SupplierAuthResponseDto>('auth/guest/activate', {
    json: {
      token: request.token,
      code: request.code,
      password: request.password,
      tcKimlikNo: request.tcKimlikNo,
    },
    auth: false,
  });

  if (dto.refreshToken) {
    useAuthStore
      .getState()
      .setSupplierSession(
        { accessToken: dto.accessToken, refreshToken: dto.refreshToken },
        terminalCompanies(dto),
      );
  }
  return dto;
}
